<ng-container *ngIf="(visible$ | async)">

	<div *ngIf="backdrop; else inlineHelpHost" class="issue-help-backdrop">
		<div class="issue-help-dialog" role="dialog" aria-modal="true" aria-label="Help article">
			<ng-container *ngTemplateOutlet="helpContent"></ng-container>
		</div>
	</div>

	<ng-template #inlineHelpHost>
		<div class="issue-help-inline">
			<div class="issue-help-dialog issue-help-dialog--inline" aria-label="Help article">
				<ng-container *ngTemplateOutlet="helpContent"></ng-container>
			</div>
		</div>
	</ng-template>

</ng-container>

<ng-template #helpContent>

	<div class="issue-help-content">

		<div class="issue-help-header">
			<div class="header-left">
				<div class="title">{{ (headerTitle$ | async) || 'Help' }}</div>

				<ng-container *ngIf="issueArticlesAll$ | async as all">
					<div class="subtitle" *ngIf="all.length">
						<ng-container *ngIf="filteredIssueArticlesAll$ | async as filtered">
							{{ filtered.length }} of {{ all.length }} articles
						</ng-container>
					</div>
				</ng-container>
			</div>

			<div class="header-right">
				<input class="search" type="search" [ngModel]="issueSearchTerm" (ngModelChange)="onSearchTermChanged($event)" placeholder="Search articles..." aria-label="Search articles" />

				<ng-content select="[fdHeaderActions]"></ng-content>

				<button *ngIf="backdrop" type="button" class="icon-btn" (click)="close()" aria-label="Close">✕</button>
			</div>
		</div>

		<div class="issue-help-body">

			<aside class="left-pane">

				<ng-container *ngIf="(issueLoading$ | async); else issueLoaded">
					<div class="loading">
						<div class="skeleton-line w-80"></div>
						<div class="skeleton-line w-95"></div>
						<div class="skeleton-line w-70"></div>
					</div>
				</ng-container>

				<ng-template #issueLoaded>
					<ng-container *ngIf="filteredIssueArticlesAll$ | async as filtered; else emptyState">
						<ng-container *ngIf="filtered.length; else emptyState">

							<ng-container *ngIf="issueSelectedArticleAll$ | async as selected; else listNoSelected">
								<div class="article-list">
									<button type="button" class="article-card" *ngFor="let article of filtered" (click)="selectIssueArticle(article)" [class.selected]="article.articleId === selected.articleId">

										<div class="article-title">{{ article.title || 'Untitled Article' }}</div>
									</button>
								</div>
							</ng-container>

							<ng-template #listNoSelected>
								<div class="article-list">
									<button type="button" class="article-card" *ngFor="let article of filtered" (click)="selectIssueArticle(article)">
										<div class="article-title">{{ article.title || 'Untitled Article' }}</div>
									</button>
								</div>
							</ng-template>

						</ng-container>
					</ng-container>

					<ng-template #emptyState>
						<ng-container *ngIf="issueArticlesAll$ | async as all">
							<ng-container *ngIf="all.length === 0; else noneFromSearch">
								<div class="empty">
									<div class="empty-title">No help articles yet</div>
									<div class="empty-subtitle">
										There are currently no help articles configured for this page.
									</div>
								</div>
							</ng-container>

							<ng-template #noneFromSearch>
								<div class="empty">
									<div class="empty-title">No articles found</div>
									<div class="empty-subtitle">Try a different search term.</div>
								</div>
							</ng-template>
						</ng-container>
					</ng-template>
				</ng-template>

			</aside>

			<main class="right-pane">

				<ng-container *ngIf="issueSelectedArticleAll$ | async as sel; else pickArticle">
					<ng-container *ngIf="sel?.bodyHtml; else pickArticle">

						<div class="viewer-shell">

							<div class="viewer-actions-compact" aria-label="Article feedback">

								<div class="va-left">
									<span class="va-label">Was this article helpful?</span>

									<div class="va-stars">
										<div class="stars" role="radiogroup" aria-label="Article rating" (mouseleave)="clearHoverRating()">
											<button type="button" class="star" *ngFor="let s of [1,2,3,4,5]" [class.filled]="(hoverRating ?? issueSelectedRating ?? 0) >= s" [class.selected]="(issueSelectedRating ?? 0) === s" (mouseenter)="setHoverRating(s)" (click)="rateIssueArticle(s)">
												<span aria-hidden="true">★</span>
											</button>
										</div>
									</div>

									<span class="va-status" *ngIf="issueSelectedRating as r; else notRated">
										{{ r }}/5 <span class="dot"></span> Saved
									</span>

									<ng-template #notRated>
										<span class="va-status muted">Optional</span>
									</ng-template>

									<button type="button" class="btn-clear-rating" *ngIf="issueSelectedRating" (click)="clearIssueArticleRating()">
										Clear rating
									</button>
								</div>

								<div class="va-right">
									<button *ngIf="isAdmin" type="button" class="btn-admin-refresh" (click)="refreshIssueArticle(sel)" [disabled]="isRefreshingArticle(sel.articleId)" [attr.aria-busy]="isRefreshingArticle(sel.articleId)">
										<span>
											{{ isRefreshingArticle(sel.articleId) ? 'Refreshing...' : 'Refresh' }}
										</span>
									</button>

									<button type="button" class="btn-feedback" (click)="openSupportForArticle()">
										<span class="btn-icon" aria-hidden="true">💬</span>
										Leave feedback
									</button>

									<div class="va-help">Tell us what’s missing</div>
								</div>

							</div>

							<app-freshdesk-solution-viewer [htmlContent]="sel.bodyHtml" [title]="sel.title" [selectedArticleId]="sel.articleId" (articleRequested)="loadIssueArticleById($event)">
							</app-freshdesk-solution-viewer>

						</div>

					</ng-container>
				</ng-container>

				<ng-template #pickArticle>
					<div class="placeholder">
						<div class="placeholder-title">Select an article</div>
						<div class="placeholder-subtitle">Choose an article on the left to view it here.</div>
					</div>
				</ng-template>

			</main>

		</div>

	</div>

</ng-template>

<ng-container *ngIf="(isSupportVisible$ | async)">
	<div class="issue-support-backdrop" [class.issue-support-backdrop--transparent]="!backdrop" (click)="closeSupport()">
		<div class="issue-support-dialog" role="dialog" aria-modal="true" aria-label="Support request" (click)="$event.stopPropagation()">
			<ng-container *ngTemplateOutlet="supportContent"></ng-container>
		</div>
	</div>
</ng-container>

<ng-template #supportContent>

	<div class="issue-support-content">

		<div class="issue-support-header">
			<div class="title-wrap">
				<div class="title">Request support</div>
				<div class="subtitle">Send feedback to support. Article and rating will be included.</div>
			</div>

			<button type="button" class="icon-btn" (click)="closeSupport()" aria-label="Close">✕</button>
		</div>

		<ng-container *ngIf="issueSelectedArticleAll$ | async as a">
			<div class="issue-support-body">

				<div class="context-grid">
					<div class="context-card">
						<div class="context-label">Article</div>
						<div class="context-value">
							{{ a?.title || 'Untitled Article' }}
							<span class="context-meta">ID: {{ a?.articleId }}</span>
						</div>
					</div>

					<div class="context-card">
						<div class="context-label">Your rating</div>
						<div class="context-value">
							{{ issueSelectedRating ? (issueSelectedRating + '/5') : 'Not rated' }}
						</div>
					</div>
				</div>

				<div class="field">
					<label class="label" for="supportEmail">Contact email <span class="hint">(optional)</span></label>
					<input id="supportEmail" class="input" type="email" [(ngModel)]="supportEmail" placeholder="name@company.com" autocomplete="email" />
					<div class="help">If we need clarification, we’ll use this to contact you.</div>
				</div>

				<div class="field">
					<label class="label" for="supportReason">What went wrong?</label>
					<select id="supportReason" class="input" [(ngModel)]="supportReason">
						<option [ngValue]="'not_found'">Article doesn't cover my issue</option>
						<option [ngValue]="'unclear'">Instructions are unclear</option>
						<option [ngValue]="'outdated'">Looks outdated</option>
						<option [ngValue]="'wrong'">Seems incorrect</option>
						<option [ngValue]="'other'">Other</option>
					</select>
				</div>

				<div class="callout" *ngIf="supportReason === 'not_found'">
					<div class="callout-title">What were you trying to do?</div>
					<div class="callout-subtitle">A quick goal statement helps us route your request faster.</div>
				</div>

				<div class="callout" *ngIf="supportReason === 'wrong' || supportReason === 'outdated'">
					<div class="callout-title">Point us to the exact spot</div>
					<div class="callout-subtitle">If you can, mention the heading or a sentence that’s wrong/outdated.</div>
				</div>

				<div class="field">
					<label class="label" for="supportDetails">
						Extra details
						<span class="pill-required" *ngIf="supportReason === 'other'">Required</span>
						<span class="hint" *ngIf="supportReason !== 'other'">(recommended)</span>
					</label>

					<textarea id="supportDetails" class="input" rows="6" [(ngModel)]="supportDetails" [attr.aria-invalid]="supportReason === 'other' && !supportDetails?.trim()" placeholder="Tell us what you expected, what you tried, and what happened. Include steps to reproduce if possible."></textarea>
				</div>

				<div class="status" *ngIf="(supportSubmitting2$ | async) || (supportSent2$ | async) || (supportError2$ | async)" role="status" aria-live="polite">
					<ng-container *ngIf="supportSubmitting2$ | async">Sending...</ng-container>
					<ng-container *ngIf="supportSent2$ | async">
						<ng-container *ngIf="supportAutoCloseIn$ | async as sec; else sentNoTimer">
							Sent. Thanks! We received your message. Closing in {{ sec }}s...
						</ng-container>

						<ng-template #sentNoTimer>
							Sent. Thanks! We received your message.
						</ng-template>
					</ng-container>

					<ng-container *ngIf="supportError2$ | async">
						{{ (supportErrorMessage2$ | async) || 'Couldn’t send. Please try again.' }}
					</ng-container>
				</div>

			</div>

			<div class="issue-support-footer">
				<button type="button" class="btn-ghost" (click)="closeSupport()" [disabled]="supportSubmitting2$ | async">
					Cancel
				</button>

				<button class="btn-primary" type="button" (click)="submitSupport()" [disabled]="supportSubmitting2$ | async">
					<span *ngIf="supportSubmitting2$ | async" class="spinner"></span>
					{{ (supportSubmitting2$ | async) ? 'Sending...' : 'Send to support' }}
				</button>
			</div>
		</ng-container>

	</div>

</ng-template>
